/**
 * EventTimeline ✦ iter233 — chronological story of every EventDrop
 * event. Reads the same /api/venues/map endpoint as VenueMap so
 * filters, geocoding and pin colours stay perfectly in sync; the
 * difference is presentation — instead of pins on a map we render a
 * vertical, year-grouped timeline with hero imagery, venue + package
 * context and a "Read the story →" link per entry.
 *
 * Props:
 *   onCount(n)  optional — called once pins load so parent can show
 *               a "showing N memories" strap.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#9C7C2E';
const PRIMARY = '#4B371E';
const TEXT_DIM = '#6B5E4F';
const IVORY = '#FFFCF6';
const HAIRLINE = 'rgba(122,92,62,0.18)';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

const mediaUrl = (path) =>
  path?.startsWith('/api/')
    ? `${process.env.REACT_APP_BACKEND_URL}${path}`
    : path;

const EventTimeline = ({ onCount }) => {
  const [searchParams] = useSearchParams();
  const initialPackage = searchParams.get('package') || '';
  const [events, setEvents] = useState([]);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState(initialPackage);
  const [yearFilter, setYearFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (eventTypeFilter) params.event_type = eventTypeFilter;
        if (packageFilter) params.package = packageFilter;
        if (yearFilter) params.year = yearFilter;
        if (search) params.search = search;
        const r = await axios.get(`${API}/venues/map`, { params });
        if (cancelled) return;
        const pins = r.data?.pins || [];
        setEvents(pins);
        if (typeof onCount === 'function') onCount(pins.length);
      } catch (e) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventTypeFilter, packageFilter, yearFilter, search, onCount]);

  // Sort newest first, then group by year/month
  const grouped = useMemo(() => {
    const sorted = [...events]
      .filter(e => e.event_date)
      .sort((a, b) => (b.event_date || '').localeCompare(a.event_date || ''));
    const out = []; // [{year, months: [{label, events}]}]
    let curYear = null; let curMonth = null; let yearBucket = null; let monthBucket = null;
    for (const ev of sorted) {
      const d = (ev.event_date || '').slice(0, 10);
      const y = d.slice(0, 4); const m = d.slice(5, 7);
      if (y !== curYear) {
        curYear = y; curMonth = null;
        yearBucket = { year: y, months: [] };
        out.push(yearBucket);
      }
      if (m !== curMonth) {
        curMonth = m;
        const label = `${MONTHS[Number(m) - 1] || ''} ${y}`;
        monthBucket = { label, events: [] };
        yearBucket.months.push(monthBucket);
      }
      monthBucket.events.push(ev);
    }
    return out;
  }, [events]);

  const eventTypes = useMemo(() => {
    const s = new Set(events.map(p => p.event_type).filter(Boolean));
    return Array.from(s).sort();
  }, [events]);
  const packages = useMemo(() => {
    const s = new Set(events.map(p => p.package_slug).filter(Boolean));
    if (packageFilter) s.add(packageFilter);
    return Array.from(s).sort();
  }, [events, packageFilter]);
  const years = useMemo(() => {
    const s = new Set(events.map(p => (p.event_date || '').slice(0, 4)).filter(Boolean));
    return Array.from(s).sort().reverse();
  }, [events]);

  return (
    <div data-testid="event-timeline">
      {/* Filter bar — mirrors the map filters so toggling views feels seamless */}
      <div data-testid="event-timeline-filters" style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        marginBottom: 24, padding: '14px 16px', background: '#FFFFFF',
        border: `1px solid ${HAIRLINE}`, borderRadius: 12,
        boxShadow: '0 12px 32px rgba(75,55,30,0.08)',
      }}>
        <input
          placeholder="Search venue, town, county…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="event-timeline-search"
          style={{
            flex: '1 1 240px', minWidth: 200, padding: '9px 13px',
            borderRadius: 8, border: '1px solid rgba(122,92,62,0.20)',
            fontFamily: SERIF, fontSize: 14, color: PRIMARY,
          }}
        />
        <TLSelect label="Event" value={eventTypeFilter} onChange={setEventTypeFilter} options={eventTypes} testid="event-timeline-filter-event" />
        <TLSelect label="Package" value={packageFilter} onChange={setPackageFilter} options={packages} testid="event-timeline-filter-package" />
        <TLSelect label="Year" value={yearFilter} onChange={setYearFilter} options={years} testid="event-timeline-filter-year" />
        <div style={{
          marginLeft: 'auto', fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12.5, color: TEXT_DIM,
        }} data-testid="event-timeline-count">
          {events.length} memor{events.length === 1 ? 'y' : 'ies'}
        </div>
      </div>

      {loading && (
        <div data-testid="event-timeline-loading" style={{
          padding: '60px 20px', textAlign: 'center', fontStyle: 'italic',
          fontFamily: SERIF, color: TEXT_DIM, fontSize: 14,
        }}>Gathering the memories…</div>
      )}

      {!loading && events.length === 0 && (
        <div data-testid="event-timeline-empty" style={{
          padding: '60px 20px', textAlign: 'center', fontStyle: 'italic',
          fontFamily: SERIF, color: TEXT_DIM, fontSize: 14,
        }}>No events found for these filters yet.</div>
      )}

      {!loading && grouped.length > 0 && (
        <div style={{
          position: 'relative', maxWidth: 920, margin: '0 auto',
          padding: '8px 0 40px',
        }}>
          {/* Vertical spine */}
          <div aria-hidden style={{
            position: 'absolute', top: 0, bottom: 0, left: 22,
            width: 2, background: `linear-gradient(180deg, ${GOLD}, rgba(156,124,46,0.08))`,
            borderRadius: 2,
          }} />
          {grouped.map(yr => (
            <section key={yr.year} data-testid={`timeline-year-${yr.year}`}>
              <h3 style={{
                fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(28px, 4vw, 38px)',
                color: PRIMARY, margin: '36px 0 14px 56px', letterSpacing: '-0.015em',
                position: 'relative',
              }}>
                <span aria-hidden style={{
                  position: 'absolute', left: -56, top: 12,
                  width: 46, height: 46, borderRadius: '50%',
                  background: GOLD, color: IVORY,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: SERIF, fontSize: 13, letterSpacing: '0.1em',
                  boxShadow: '0 6px 18px rgba(156,124,46,0.4)',
                }}>{yr.year.slice(-2)}</span>
                {yr.year}
              </h3>
              {yr.months.map(m => (
                <div key={m.label}>
                  <div style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: GOLD, margin: '16px 0 12px 56px',
                  }}>{m.label}</div>
                  {m.events.map(ev => (
                    <TimelineCard key={ev.event_id} ev={ev} />
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

const TimelineCard = ({ ev }) => {
  const colour = ev.pin_colour || GOLD;
  const date = (ev.event_date || '').slice(0, 10);
  return (
    <article data-testid={`timeline-card-${ev.event_id}`} style={{
      position: 'relative', marginLeft: 56, marginBottom: 18,
      background: '#FFFFFF', border: `1px solid ${HAIRLINE}`,
      borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 10px 28px rgba(75,55,30,0.10)',
      display: 'grid', gridTemplateColumns: ev.cover_image ? '200px 1fr' : '1fr',
    }}>
      {/* Dot on the spine */}
      <span aria-hidden style={{
        position: 'absolute', left: -41, top: 28,
        width: 14, height: 14, borderRadius: '50%',
        background: colour, border: '3px solid #FFF8E7',
        boxShadow: `0 0 0 2px ${colour}30, 0 4px 10px rgba(0,0,0,0.18)`,
      }} />
      {ev.cover_image && (
        <Link to={`/events/${ev.slug}`} style={{
          backgroundImage: `url(${mediaUrl(ev.cover_image)})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          minHeight: 160, display: 'block',
        }} aria-label={`Open ${ev.title}`} />
      )}
      <div style={{ padding: '18px 22px', fontFamily: SERIF, color: PRIMARY }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
        }}>
          <h4 style={{
            margin: 0, fontWeight: 500, fontSize: 19,
            letterSpacing: '-0.01em', color: PRIMARY,
          }}>{ev.venue_name || ev.title}</h4>
          {ev.is_featured && (
            <span style={{
              background: GOLD, color: IVORY, fontFamily: SERIF,
              fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
              padding: '3px 9px', borderRadius: 999,
            }}>★ Featured</span>
          )}
        </div>
        <div style={{
          fontStyle: 'italic', fontSize: 12.5, color: TEXT_DIM, marginTop: 4,
        }}>
          {[date, ev.county, ev.event_type, ev.package_label || ev.package_slug]
            .filter(Boolean).join(' · ')}
        </div>
        {ev.rating > 0 && (
          <div style={{
            color: GOLD, fontSize: 14, letterSpacing: 1.5, marginTop: 8,
          }}>{'★'.repeat(Math.round(ev.rating))}</div>
        )}
        {ev.review_snippet && (
          <p style={{
            fontStyle: 'italic', fontSize: 13.5, lineHeight: 1.65,
            margin: '10px 0 6px', color: PRIMARY,
          }}>
            &ldquo;{ev.review_snippet.slice(0, 180)}
            {ev.review_snippet.length > 180 ? '…' : ''}&rdquo;
          </p>
        )}
        <div style={{ marginTop: 12, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <Link to={`/events/${ev.slug}`} data-testid={`timeline-card-link-${ev.event_id}`} style={{
            color: GOLD, fontSize: 13, fontStyle: 'italic',
            textDecoration: 'underline', textUnderlineOffset: 3,
          }}>Read the story →</Link>
          {ev.venue_slug && (
            <Link to={`/venues/${ev.venue_slug}`} style={{
              color: TEXT_DIM, fontSize: 13, fontStyle: 'italic',
              textDecoration: 'underline', textUnderlineOffset: 3,
            }}>More at {ev.venue_name || 'this venue'} →</Link>
          )}
        </div>
      </div>
    </article>
  );
};

const TLSelect = ({ label, value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    style={{
      padding: '9px 12px', borderRadius: 8, fontFamily: SERIF, fontSize: 13.5,
      border: '1px solid rgba(122,92,62,0.20)', color: PRIMARY, background: '#FFFFFF',
      cursor: 'pointer',
    }}>
    <option value="">All {label.toLowerCase()}s</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default EventTimeline;
